import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaYoutube,
  FaFacebookSquare,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaClock,
  FaThumbsUp,
  FaCommentDots,
  FaShareAlt,
  FaPowerOff,
  FaLink,
  FaExternalLinkAlt,
  FaCopy,
  FaPlay,
  FaPause,
  FaCheckCircle,
  FaInbox,
  FaUsers,
  FaPhoneAlt,
  FaWhatsapp,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaImage,
  FaExclamationTriangle,
  FaFileCsv,
  FaTrash,
  FaUserSlash,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= settings ================= */

// Watch time from the app is stored in milliseconds; package target is in hours.
// If it turns out to be seconds, change this to 1000.
const WATCH_UNIT_PER_SECOND = 1000;

/* ================= styles ================= */

const CSS = `
@keyframes alFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes alFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes alSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes alBar { from { width: 0; } }
@keyframes alPulse { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,.55); } 70% { box-shadow: 0 0 0 7px rgba(16,185,129,0); } 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); } }
.al-in { animation: alFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.al-fade { animation: alFade .2s ease both; }
.al-slide { animation: alSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.al-bar { animation: alBar .9s cubic-bezier(.2,.7,.2,1) both; }
.al-live { animation: alPulse 1.8s ease-out infinite; }
.al-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.al-press:hover { transform: translateY(-1px); }
.al-press:active { transform: scale(.96); }
.al-card { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease; }
.al-card:hover { transform: translateY(-4px); box-shadow: 0 20px 36px -20px rgba(15,23,42,.45); }
.al-thumb img { transition: transform .5s cubic-bezier(.2,.8,.2,1); }
.al-card:hover .al-thumb img { transform: scale(1.06); }
.al-knob { transition: transform .25s cubic-bezier(.2,.8,.2,1); }
.al-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .al-in, .al-fade, .al-slide, .al-bar, .al-live { animation: none; }
  .al-press:hover, .al-press:active, .al-card:hover, .al-card:hover .al-thumb img { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400";

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const on = (c) => String(c.monitization) === "1" || c.monitization === 1 || c.monitization === true;
const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};
const waLink = (p) => {
  const d = String(p || "").replace(/\D/g, "").slice(-10);
  return d ? `https://wa.me/91${d}` : null;
};
const watchSeconds = (c) => num(c.watch_time) / WATCH_UNIT_PER_SECOND;
const fmtDuration = (sec) => {
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

// the four things a package promises, with done / target
const partsOf = (c) => [
  { key: "watch", label: "Watch", icon: FaClock, done: watchSeconds(c) / 3600, target: num(c.target_watch_time), text: `${fmtDuration(watchSeconds(c))} / ${num(c.target_watch_time)}h`, color: "bg-sky-500" },
  { key: "likes", label: "Likes", icon: FaThumbsUp, done: num(c.likes_count), target: num(c.target_likes), text: `${num(c.likes_count)} / ${num(c.target_likes)}`, color: "bg-rose-500" },
  { key: "comments", label: "Comments", icon: FaCommentDots, done: num(c.comments_count), target: num(c.target_comments), text: `${num(c.comments_count)} / ${num(c.target_comments)}`, color: "bg-amber-500" },
  { key: "share", label: "Shares", icon: FaShareAlt, done: num(c.share_count), target: num(c.target_share), text: `${num(c.share_count)} / ${num(c.target_share)}`, color: "bg-emerald-500" },
];
const pctOf = (p) => (p.target > 0 ? Math.min(100, (p.done / p.target) * 100) : null);
const overallOf = (c) => {
  const ps = partsOf(c).map(pctOf).filter((x) => x !== null);
  return ps.length ? Math.round(ps.reduce((s, x) => s + x, 0) / ps.length) : null;
};
const statusOf = (c) => {
  if (!c.link) return "nolink";
  if (overallOf(c) === 100) return "done";
  return on(c) ? "running" : "paused";
};
const STATUS = {
  running: { label: "Running", cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  paused: { label: "Paused", cls: "bg-slate-200 text-slate-700", dot: "bg-slate-400" },
  done: { label: "Completed", cls: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  nolink: { label: "No link yet", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
};

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="al-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

/* ================= page ================= */

const GroupActiveLinks = () => {
  const { platform } = useParams();
  const navigate = useNavigate();
  const isYT = platform === "youtube";
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("least");
  const [page, setPage] = useState(1);
  const [checked, setChecked] = useState([]);
  const [busy, setBusy] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [proofs, setProofs] = useState({});
  const [linkDraft, setLinkDraft] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [refs, setRefs] = useState({}); // cust_id -> { user_id, user_name }
  const [confirmDel, setConfirmDel] = useState(null); // list of customers
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const PAGE = 12;

  const toast = useRef(null);
  const searchRef = useRef(null);

  const theme = isYT
    ? { name: "YouTube", Icon: FaYoutube, hero: "from-red-600 via-rose-700 to-slate-900", accent: "bg-red-600 hover:bg-red-700", ring: "ring-red-400", text: "text-red-600" }
    : { name: "Facebook", Icon: FaFacebookSquare, hero: "from-blue-600 via-indigo-700 to-slate-900", accent: "bg-blue-600 hover:bg-blue-700", ring: "ring-blue-400", text: "text-blue-600" };

  /* ----- API ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/customer.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  // The app's own API (same server) has the proof list and link update
  const appApi = useMemo(() => String(baseUrl).replace(/\/admin\/api\/?$/, "/APIs"), [baseUrl]);
  const postApp = useCallback(
    async (op, extra = {}) => {
      const fd = new FormData();
      fd.append("API_KEY", "YCNP2024");
      fd.append("op", op);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${appApi}/customer.php`, { method: "POST", body: fd });
      return res.json();
    },
    [appApi]
  );

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const [res, list] = await Promise.all([post("customerProfile", { cust_type: isYT ? "YouTube" : "Facebook" }), post("customerList").catch(() => null)]);
      setCustomers(res?.error === 0 && Array.isArray(res.customers) ? res.customers : []);
      const rm = {};
      (list?.customers || []).forEach((x) => (rm[String(x.cust_id)] = { user_id: x.user_id, user_name: x.user_name }));
      setRefs(rm);
      setProofs({});
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load links" });
    }
    setLoading(false);
  }, [post, isYT]);

  useEffect(() => {
    setQuery("");
    setStatus("all");
    setChecked([]);
    setDrawer(null);
    fetchCustomers();
  }, [fetchCustomers]);

  // customer was brought by a user whose account no longer exists
  const refGone = (c) => {
    const r = refs[String(c.cust_id)];
    return !!r && r.user_id && r.user_id !== "null" && r.user_id !== "NULL" && !(r.user_name && String(r.user_name).trim());
  };

  const runDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    let ok = 0;
    let needsServer = false;
    for (const c of confirmDel) {
      try {
        const res = await post("deleteCustomer", { cust_id: c.cust_id });
        if (res?.error !== 0) {
          if (String(res?.error || "").toLowerCase().includes("invalid action")) needsServer = true;
          throw new Error(res?.message || "Failed");
        }
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setDeleting(false);
    setConfirmDel(null);
    setDeleteText("");
    setChecked([]);
    setDrawer(null);
    if (ok) toast.current?.show({ severity: "success", summary: "Deleted", detail: `${ok} customer${ok > 1 ? "s" : ""} removed` });
    if (needsServer) toast.current?.show({ severity: "warn", summary: "Server update needed", detail: "Add the 'deleteCustomer' action to admin/api/customer.php on the server.", life: 8000 });
    else if (ok < confirmDel.length) toast.current?.show({ severity: "error", summary: "Error", detail: `${confirmDel.length - ok} could not be deleted` });
    fetchCustomers();
  };

  const askDelete = (list) => {
    setDeleteText("");
    setConfirmDel(list);
  };

  const setMon = async (c, value) => {
    setBusy(c.cust_id);
    try {
      const res = await post("toggleMonitization", { cust_id: c.cust_id, monitization: value ? "1" : "0" });
      if (res?.error !== 0) throw new Error(res?.message || "Failed");
      setCustomers((l) => l.map((x) => (x.cust_id === c.cust_id ? { ...x, monitization: value ? "1" : "0" } : x)));
      return true;
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: `${c.name}: ${e.message}` });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const toggleOne = async (c) => {
    const next = !on(c);
    if (await setMon(c, next)) toast.current?.show({ severity: "success", summary: next ? "Running" : "Paused", detail: `${c.name}'s link is ${next ? "now shown to" : "hidden from"} members`, life: 2200 });
  };

  const bulk = async (value) => {
    const list = customers.filter((c) => checked.includes(c.cust_id) && on(c) !== value);
    let ok = 0;
    for (const c of list) if (await setMon(c, value)) ok += 1;
    setChecked([]);
    if (ok) toast.current?.show({ severity: "success", summary: "Done", detail: `${ok} link${ok > 1 ? "s" : ""} ${value ? "started" : "paused"}` });
  };

  const openDrawer = async (c) => {
    setDrawer(c.cust_id);
    setLinkDraft(c.link || "");
    if (proofs[c.cust_id] && proofs[c.cust_id] !== "error") return;
    setProofs((p) => ({ ...p, [c.cust_id]: "loading" }));
    try {
      const res = await postApp("customerMontitizationProof", { cust_id: c.cust_id });
      setProofs((p) => ({ ...p, [c.cust_id]: Array.isArray(res?.proof_list) ? res.proof_list : [] }));
    } catch (e) {
      setProofs((p) => ({ ...p, [c.cust_id]: "error" }));
    }
  };

  const saveLink = async (c) => {
    const link = linkDraft.trim();
    if (!/^https?:\/\/\S+$/i.test(link)) {
      toast.current?.show({ severity: "error", summary: "Invalid link", detail: "Paste a full link starting with https://" });
      return;
    }
    setSavingLink(true);
    try {
      const res = await postApp("addCustomerLink", { cust_id: c.cust_id, link });
      if (res?.error !== 0) throw new Error(res?.error || "Failed");
      setCustomers((l) => l.map((x) => (x.cust_id === c.cust_id ? { ...x, link } : x)));
      toast.current?.show({ severity: "success", summary: "Link saved", detail: c.name });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: String(e.message) });
    }
    setSavingLink(false);
  };

  /* ----- derived ----- */

  const q = query.trim();

  const counts = useMemo(() => {
    const m = { all: customers.length, running: 0, paused: 0, done: 0, nolink: 0 };
    customers.forEach((c) => (m[statusOf(c)] += 1));
    return m;
  }, [customers]);

  const totals = useMemo(
    () => ({
      watch: customers.reduce((s, c) => s + watchSeconds(c), 0),
      likes: customers.reduce((s, c) => s + num(c.likes_count), 0),
      comments: customers.reduce((s, c) => s + num(c.comments_count), 0),
      shares: customers.reduce((s, c) => s + num(c.share_count), 0),
    }),
    [customers]
  );

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let l = customers.filter((c) => {
      if (status === "unknown") {
        if (!refGone(c)) return false;
      } else if (status !== "all" && statusOf(c) !== status) return false;
      if (!ql) return true;
      return [c.cust_id, c.name, c.contact_num, c.pkg_name, c.link].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "least") return (overallOf(a) ?? -1) - (overallOf(b) ?? -1);
      if (sortBy === "most") return (overallOf(b) ?? -1) - (overallOf(a) ?? -1);
      const da = parseAny(a.date_time)?.getTime() || 0;
      const db = parseAny(b.date_time)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, q, status, sortBy, refs]);

  useEffect(() => setPage(1), [q, status, sortBy]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);
  const current = customers.find((c) => c.cust_id === drawer) || null;

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (e.key === "Escape") {
        setConfirmDel((x) => (deleting ? x : null));
        setDrawer(null);
      }
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleting]);

  const copyText = (t, label = "Copied") => {
    navigator.clipboard?.writeText(String(t));
    toast.current?.show({ severity: "info", summary: label, detail: String(t), life: 1500 });
  };

  const exportCsv = () => {
    const head = ["Cust ID", "Name", "Contact", "Package", "Link", "Watch (sec)", "Watch target (h)", "Likes", "Likes target", "Comments", "Comments target", "Shares", "Shares target", "Progress %", "Status", "Date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = filtered.map((c) =>
      [c.cust_id, c.name, c.contact_num, c.pkg_name, c.link, Math.round(watchSeconds(c)), c.target_watch_time, c.likes_count, c.target_likes, c.comments_count, c.target_comments, c.share_count, c.target_share, overallOf(c) ?? "", STATUS[statusOf(c)].label, c.date_time]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-${platform}-links-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ----- pieces ----- */

  const Switch = ({ c }) => (
    <button
      type="button"
      role="switch"
      aria-checked={on(c)}
      disabled={busy === c.cust_id}
      onClick={(e) => {
        e.stopPropagation();
        toggleOne(c);
      }}
      title={on(c) ? "Monetization on. Click to pause." : "Monetization off. Click to start."}
      className={`relative w-12 h-7 rounded-full transition-colors disabled:opacity-60 ${on(c) ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
    >
      <span className={`al-knob absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center ${on(c) ? "translate-x-5" : ""}`}>
        {busy === c.cust_id ? <FaSyncAlt className="animate-spin text-slate-400" size={9} /> : <FaPowerOff size={9} className={on(c) ? "text-emerald-600" : "text-slate-400"} />}
      </span>
    </button>
  );

  const card = (c, i) => {
    const st = statusOf(c);
    const S = STATUS[st];
    const overall = overallOf(c);
    const id = ytId(c.link);
    const sel = checked.includes(c.cust_id);
    const d = parseAny(c.date_time);
    return (
      <article
        key={c.cust_id}
        onClick={() => openDrawer(c)}
        className={`al-card al-in cursor-pointer rounded-2xl bg-white dark:bg-secondary-dark-bg border overflow-hidden flex flex-col ${sel ? `border-transparent ring-2 ${theme.ring}` : "border-slate-200 dark:border-slate-700"}`}
        style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
      >
        {/* thumbnail */}
        <div className="al-thumb relative aspect-video bg-slate-900 overflow-hidden">
          {id ? (
            <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className={`w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br ${theme.hero} text-white/80`}>
              {c.link ? <theme.Icon size={34} /> : <FaLink size={26} />}
              <span className="text-xs">{c.link ? "Link added" : "Customer has not added a link"}</span>
            </div>
          )}
          <div className="absolute inset-x-0 top-0 p-2.5 flex items-center justify-between">
            <label onClick={(e) => e.stopPropagation()} className="w-7 h-7 rounded-lg bg-white/90 flex items-center justify-center cursor-pointer">
              <input type="checkbox" checked={sel} onChange={() => setChecked((x) => (x.includes(c.cust_id) ? x.filter((y) => y !== c.cust_id) : [...x, c.cust_id]))} className="w-4 h-4 accent-slate-800" aria-label="Select" />
            </label>
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full shadow ${S.cls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${S.dot} ${st === "running" ? "al-live" : ""}`} /> {S.label}
            </span>
          </div>
          {c.link && (
            <a href={c.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" aria-label="Open link">
              <FaPlay size={14} />
            </a>
          )}
          {overall !== null && (
            <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/30">
              <div className={`al-bar h-full ${overall === 100 ? "bg-sky-400" : "bg-emerald-400"}`} style={{ width: `${overall}%` }} />
            </div>
          )}
        </div>

        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white truncate">
                <Hi text={c.name} q={q} />
              </p>
              <p className="text-xs text-slate-500 tabular-nums truncate">
                <Hi text={c.contact_num} q={q} /> • #<Hi text={c.cust_id} q={q} />
              </p>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 mt-0.5">
                <Hi text={c.pkg_name} q={q} />
              </p>
              {refGone(c) && (
                <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                  <FaUserSlash size={8} /> REFERRER DELETED
                </span>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className={`text-2xl font-black tabular-nums ${overall === 100 ? "text-sky-600" : "text-slate-900 dark:text-white"}`}>{overall === null ? "–" : `${overall}%`}</p>
              <p className="text-[10px] text-slate-500">delivered</p>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            {partsOf(c).map((p) => {
              const pct = pctOf(p);
              const PI = p.icon;
              return (
                <div key={p.key} className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                  <p className="flex items-center justify-between text-[10px] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <PI size={9} /> {p.label}
                    </span>
                    {pct === 100 && <FaCheckCircle size={9} className="text-sky-500" />}
                  </p>
                  <p className="text-xs font-bold tabular-nums text-slate-900 dark:text-white">{p.target ? p.text : "–"}</p>
                  <div className="mt-1 h-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div className={`al-bar h-full ${pct === 100 ? "bg-sky-500" : p.color}`} style={{ width: `${pct || 0}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-auto pt-3 flex items-center justify-between gap-2">
            <p className="text-[11px] text-slate-400">
              {fmtDate(d)}, {fmtTime(d)}
            </p>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={() => askDelete([c])} title="Delete customer" aria-label="Delete customer" className={`al-press h-8 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold ${st === "done" || refGone(c) ? "px-2.5 bg-rose-600 text-white hover:bg-rose-700" : "w-8 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white"}`}>
                <FaTrash size={10} /> {(st === "done" || refGone(c)) && "Delete"}
              </button>
              <span className={`text-xs font-semibold ${on(c) ? "text-emerald-600" : "text-slate-400"}`}>{on(c) ? "On" : "Off"}</span>
              <Switch c={c} />
            </div>
          </div>
        </div>
      </article>
    );
  };

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="al-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Customer</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{theme.name} Active Links</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Customer videos that group members watch, like, comment and share. Turn a link on to show it to members.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
            {[
              { id: "youtube", label: "YouTube", icon: FaYoutube, cls: "text-red-600" },
              { id: "facebook", label: "Facebook", icon: FaFacebookSquare, cls: "text-blue-600" },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} type="button" onClick={() => navigate(`/customer/link/active/${t.id}`)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${platform === t.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                  <Icon className={t.cls} size={12} /> {t.label}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={exportCsv} disabled={!filtered.length} className="al-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50">
            <FaFileCsv /> Export
          </button>
          <button type="button" onClick={fetchCustomers} disabled={loading} className={`al-press inline-flex items-center gap-2 h-9 px-4 rounded-xl text-white text-sm font-semibold disabled:opacity-60 ${theme.accent}`}>
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className={`al-in relative overflow-hidden rounded-3xl bg-gradient-to-br ${theme.hero} text-white p-6`}>
        <div className="pointer-events-none absolute -right-10 -top-10 w-60 h-60 rounded-full bg-white/10 blur-3xl" />
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { id: "running", icon: FaPlay },
            { id: "paused", icon: FaPause },
            { id: "done", icon: FaCheckCircle },
            { id: "nolink", icon: FaLink },
          ].map((s) => {
            const Icon = s.icon;
            const act = status === s.id;
            return (
              <button key={s.id} type="button" onClick={() => setStatus(act ? "all" : s.id)} className={`al-press text-left rounded-2xl p-4 border ${act ? "bg-white text-slate-900 border-white" : "bg-white/10 border-white/10"}`}>
                <p className={`text-xs inline-flex items-center gap-1.5 ${act ? "text-slate-600" : "text-white/80"}`}>
                  <Icon size={10} /> {STATUS[s.id].label}
                </p>
                <p className="text-3xl font-black tabular-nums">{loading && !customers.length ? "…" : counts[s.id]}</p>
              </button>
            );
          })}
        </div>
        <div className="relative mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/85">
          <span className="inline-flex items-center gap-1.5">
            <FaUsers size={12} /> {counts.all} customers
          </span>
          <span className="inline-flex items-center gap-1.5">
            <FaClock size={12} /> {fmtDuration(totals.watch)} watched
          </span>
          <span className="inline-flex items-center gap-1.5">
            <FaThumbsUp size={12} /> {totals.likes} likes
          </span>
          <span className="inline-flex items-center gap-1.5">
            <FaCommentDots size={12} /> {totals.comments} comments
          </span>
          <span className="inline-flex items-center gap-1.5">
            <FaShareAlt size={12} /> {totals.shares} shares
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`${CARD} al-in mt-4 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "120ms" }}>
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={13} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, customer ID, package or link" className={`${INPUT} w-full h-10 pl-10 pr-9`} />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
              <FaTimes size={12} />
            </button>
          )}
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${INPUT} h-10 px-3`}>
          <option value="all">All ({counts.all})</option>
          {Object.entries(STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label} ({counts[k]})
            </option>
          ))}
          <option value="unknown">Referrer deleted ({customers.filter(refGone).length})</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-10 px-3`}>
          <option value="least">Least delivered first</option>
          <option value="most">Most delivered first</option>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
        {checked.length === 0 && (counts.done > 0 || customers.some(refGone)) && (
          <div className="flex items-center gap-2 ml-auto text-sm">
            {counts.done > 0 && (
              <button type="button" onClick={() => setChecked(customers.filter((c) => statusOf(c) === "done").map((c) => c.cust_id))} className="al-press h-9 px-3 rounded-xl border border-sky-200 text-sky-700 bg-sky-50 font-semibold">
                Select {counts.done} completed
              </button>
            )}
            {customers.some(refGone) && (
              <button type="button" onClick={() => setChecked(customers.filter(refGone).map((c) => c.cust_id))} className="al-press h-9 px-3 rounded-xl border border-rose-200 text-rose-700 bg-rose-50 font-semibold">
                Select {customers.filter(refGone).length} unknown
              </button>
            )}
          </div>
        )}
        {checked.length > 0 && (
          <div className="al-fade flex items-center gap-2 ml-auto">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{checked.length} selected</span>
            <button type="button" onClick={() => bulk(true)} className="al-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
              <FaPlay size={9} /> Start
            </button>
            <button type="button" onClick={() => bulk(false)} className="al-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold">
              <FaPause size={9} /> Pause
            </button>
            <button type="button" onClick={() => askDelete(customers.filter((c) => checked.includes(c.cust_id)))} className="al-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold">
              <FaTrash size={9} /> Delete
            </button>
            <button type="button" onClick={() => setChecked([])} className="text-sm text-slate-500 hover:underline">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="mt-4">
        {loading && !customers.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`${CARD} h-96 animate-pulse`} />
            ))}
          </div>
        ) : !filtered.length ? (
          <div className={`${CARD} px-5 py-16 text-center`}>
            <FaInbox className="mx-auto text-slate-300" size={36} />
            <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{customers.length ? "Nothing matches" : `No ${theme.name} customers yet`}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">{pageRows.map(card)}</div>
        )}
        {filtered.length > PAGE && (
          <div className="flex items-center justify-center gap-3 mt-5 text-sm">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="al-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
              <FaChevronLeft size={11} />
            </button>
            <span className="tabular-nums text-slate-600 dark:text-slate-300">
              Page <b>{safePage}</b> of {totalPages}
            </span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="al-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
              <FaChevronRight size={11} />
            </button>
          </div>
        )}
      </div>

      {/* ================= Drawer ================= */}
      {current && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="al-fade absolute inset-0 bg-slate-900/40" onClick={() => setDrawer(null)} />
          <aside className="al-slide relative w-full max-w-xl h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="min-w-0">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[statusOf(current)].cls}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS[statusOf(current)].dot}`} /> {STATUS[statusOf(current)].label}
                </span>
                <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white truncate">{current.name}</h2>
                <p className="text-xs text-slate-500 tabular-nums">
                  #{current.cust_id} • {current.pkg_name}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => askDelete([current])} className="al-press inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white text-xs font-semibold">
                  <FaTrash size={10} /> Delete
                </button>
                <Switch c={current} />
                <button type="button" onClick={() => setDrawer(null)} className="al-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* player */}
              {ytId(current.link) ? (
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe title="video" src={`https://www.youtube.com/embed/${ytId(current.link)}`} className="w-full h-full" allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              ) : null}

              {/* link editor */}
              <section>
                <p className="text-xs font-semibold text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
                  <FaLink size={10} /> Video link
                </p>
                <div className="flex gap-2">
                  <input value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)} placeholder="https://..." className={`${INPUT} flex-1 h-10 px-3`} />
                  {current.link && (
                    <>
                      <button type="button" onClick={() => copyText(current.link, "Link copied")} className="al-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600" aria-label="Copy link">
                        <FaCopy size={12} />
                      </button>
                      <a href={current.link} target="_blank" rel="noreferrer" className="al-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600" aria-label="Open link">
                        <FaExternalLinkAlt size={11} />
                      </a>
                    </>
                  )}
                </div>
                {linkDraft.trim() !== String(current.link || "") && (
                  <button type="button" onClick={() => saveLink(current)} disabled={savingLink} className={`al-press mt-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-xl text-white text-sm font-semibold disabled:opacity-60 ${theme.accent}`}>
                    {savingLink ? <FaSyncAlt className="animate-spin" size={11} /> : <FaEdit size={11} />} {current.link ? "Change link" : "Save link"}
                  </button>
                )}
              </section>

              {/* progress */}
              <section className="grid grid-cols-2 gap-2">
                {partsOf(current).map((p) => {
                  const pct = pctOf(p);
                  const PI = p.icon;
                  return (
                    <div key={p.key} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                        <PI size={10} /> {p.label}
                      </p>
                      <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">{p.target ? p.text : "Not in package"}</p>
                      <div className="mt-1.5 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div className={`al-bar h-full ${pct === 100 ? "bg-sky-500" : p.color}`} style={{ width: `${pct || 0}%` }} />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1 tabular-nums">{pct === null ? "" : `${Math.round(pct)}%`}</p>
                    </div>
                  );
                })}
              </section>

              {/* contact */}
              <section className="flex flex-wrap items-center gap-2 text-sm">
                <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
                  <FaPhoneAlt size={11} className="text-slate-400" /> {current.contact_num}
                </span>
                {waLink(current.contact_num) && (
                  <a href={waLink(current.contact_num)} target="_blank" rel="noreferrer" className="al-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold">
                    <FaWhatsapp /> WhatsApp
                  </a>
                )}
                <button type="button" onClick={() => window.open(`https://customer.ycnp.in/customer/${current.cust_id}`, "_blank")} className="al-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200">
                  Customer page <FaExternalLinkAlt size={9} />
                </button>
              </section>

              {/* who did the work */}
              <section>
                <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2 inline-flex items-center gap-2">
                  <FaUsers className="text-slate-400" /> Members who worked on this link
                </p>
                {proofs[current.cust_id] === "loading" || !proofs[current.cust_id] ? (
                  <div className="space-y-2">
                    {[0, 1].map((i) => (
                      <div key={i} className="h-14 rounded-xl bg-slate-100 dark:bg-slate-700 animate-pulse" />
                    ))}
                  </div>
                ) : proofs[current.cust_id] === "error" ? (
                  <p className="text-sm text-rose-600 inline-flex items-center gap-1.5">
                    <FaExclamationTriangle size={11} /> Could not load the proof list.
                  </p>
                ) : !proofs[current.cust_id].length ? (
                  <p className="text-sm text-slate-500">No member has worked on this link yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {proofs[current.cust_id].map((pr) => {
                      const dd = parseAny(pr.date_time);
                      const secs = (num(pr.view_end) - num(pr.view_start)) / WATCH_UNIT_PER_SECOND;
                      return (
                        <li key={pr.proof_id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                          <div className="flex items-center gap-3">
                            {pr.profile_img && !/\/$/.test(pr.profile_img) ? (
                              <img src={pr.profile_img} alt="" className="w-9 h-9 rounded-full object-cover bg-slate-100" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                            ) : (
                              <span className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-600" />
                            )}
                            <div className="flex-1 min-w-0">
                              <button type="button" onClick={() => navigate(`/user-profile/${pr.user_id}`)} className="text-sm font-semibold text-slate-900 dark:text-white truncate hover:underline text-left">
                                {pr.user_name && String(pr.user_name).trim() ? pr.user_name : `Unknown user (${pr.user_id})`}
                              </button>
                              <p className="text-[11px] text-slate-500">
                                {fmtDate(dd)}, {fmtTime(dd)}
                                {secs > 0 && ` • watched ${fmtDuration(secs)}`}
                              </p>
                            </div>
                          </div>
                          {[pr.likes_proof, pr.comment_proof, pr.share_proof].some(Boolean) && (
                            <div className="mt-2 flex gap-2">
                              {[
                                { url: pr.likes_proof, label: "Like" },
                                { url: pr.comment_proof, label: "Comment" },
                                { url: pr.share_proof, label: "Share" },
                              ]
                                .filter((x) => x.url)
                                .map((x) => (
                                  <a key={x.label} href={x.url} target="_blank" rel="noreferrer" className="group relative w-20 h-14 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                                    <img src={x.url} alt={`${x.label} proof`} className="absolute inset-0 w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                                    <FaImage className="text-slate-400" />
                                    <span className="absolute inset-x-0 bottom-0 text-[9px] font-bold text-white bg-black/60 text-center">{x.label}</span>
                                  </a>
                                ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </aside>
        </div>
      )}
      {/* ================= Delete confirm ================= */}
      {confirmDel && confirmDel.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="al-fade absolute inset-0 bg-slate-900/60" onClick={() => !deleting && setConfirmDel(null)} />
          <div className="al-in relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center">
              <FaExclamationTriangle size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{confirmDel.length === 1 ? `Delete ${confirmDel[0].name}?` : `Delete ${confirmDel.length} customers?`}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">The customer record is removed for good and the link stops showing to members. Money already paid and commission already given are not changed.</p>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirmDel.slice(0, 30).map((c) => (
                <div key={c.cust_id} className="flex items-center justify-between gap-3 p-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 tabular-nums">
                      #{c.cust_id} • {STATUS[statusOf(c)].label}
                      {refGone(c) && " • referrer deleted"}
                    </p>
                  </div>
                  <span className="text-sm font-bold tabular-nums">{overallOf(c) === null ? "–" : `${overallOf(c)}%`}</span>
                </div>
              ))}
              {confirmDel.length > 30 && <p className="p-2.5 text-xs text-slate-500">and {confirmDel.length - 30} more</p>}
            </div>
            {confirmDel.some((c) => statusOf(c) === "running") && (
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-xs p-3">
                <FaExclamationTriangle className="mt-0.5 shrink-0" /> Some of these are still running and not completed. The customer paid for this work.
              </p>
            )}
            <div className="mt-4">
              <label htmlFor="al-del" className="text-sm text-slate-700 dark:text-slate-200">
                Type <b>DELETE</b> to confirm
              </label>
              <input id="al-del" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus placeholder="DELETE" className={`${INPUT} w-full h-10 px-3 mt-1.5`} />
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirmDel(null)} disabled={deleting} className="al-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Cancel
              </button>
              <button type="button" onClick={runDelete} disabled={deleting || deleteText.trim().toUpperCase() !== "DELETE"} className="al-press flex-1 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {deleting ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {deleting ? "Deleting..." : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupActiveLinks;